/*
 * COPYRIGHT. HSBC HOLDINGS PLC 2017. ALL RIGHTS RESERVED.
 *
 * This software is only to be used for the purpose for which it has been
 * provided. No part of it is to be reproduced, disassembled, transmitted,
 * stored in a retrieval system nor translated in any human or computer
 * language in any way or for any other purposes whatsoever without the
 * prior written consent of HSBC Holdings plc.
 *
 */

/*
// Author: Grin Zhou
*/

import React from 'react';
import PropTypes from 'prop-types';
import BaseComponent from '../BaseComponent';
import { Row, Col, FormGroup, HelpBlock } from 'react-bootstrap';
import InputText from '../../../../shared/AOForm/components/InputText/InputText';
import ItemTitle from '../ItemTitle';
import './DateField.css';

export default class DateField extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      dayFieldValue: null,
      monthFieldValue: null,
      isDayFocus: false,
      isMonthFocus: false,
      isYearFocus: false
    };

    [
      'valueDidChange',
      'handleDates',
      'onBlurAction'
    ].forEach((fn) => this[fn] = this[fn].bind(this));
  }

  valueDidChange = (values, fieldType) => {
    /*
    * 1. when user had input 2 numbers in day or month field, cursor should be move to next field automatically.
    * 2. formatting data.
    * 3. callback the onChange action.
    */

    const { dateChangeAction, questionRel } = this.props;
    const finalValue = values.join("-");

    switch (fieldType) {
      case 0:
        if (values[0].length === 2) {
          document.getElementById(questionRel.questionId + '_monthField').focus();
        }
        break;
      case 1:
        if (values[1].length === 2) {
          document.getElementById(questionRel.questionId + '_yearField').focus();
        }
        break;
      default:
        break;
    }

    dateChangeAction && dateChangeAction(questionRel, finalValue)
  };

  handleDates = (caseIndex) => (rel, val) => {
    /*
    * formatting and set array data.
    */

    const {
      dayContent,
      monthContent,
      yearContent,
    } = this.props;
    const day = dayContent.textValue;
    const month = monthContent.textValue;
    const year = yearContent.textValue;
    switch (caseIndex) {
      case 0:
        this.valueDidChange([val, month, year], 0);
        break;
      case 1:
        this.valueDidChange([day, val, year], 1);
        break;
      case 2:
        this.valueDidChange([day, month, val], 2);
        break;
      default:
        break;
    }
  };

  onFocusAction = (caseIndex) => () => {
    /*
    * when cursor focus in the field, set the focus status, and reset field value.
    */

    switch (caseIndex) {
      case 0:
        this.setState({ isDayFocus: true, dayFieldValue: null });
        break;
      case 1:
        this.setState({ isMonthFocus: true, monthFieldValue: null });
        break;
      case 2:
        this.setState({ isYearFocus: true });
        break;
      default:
        break;
    }
  };

  onBlurAction = (caseIndex) => (rel, targetVal) => {
    /*
    * 1. when user input only one number, add "0" before it (day/month).
    * 2. validation checking when cursor leave all fields.
    * 3. callback the onBlur action.
    */

    const {
      blurAction
    } = this.props;

    const isFocus = () => {
      const { isDayFocus, isMonthFocus, isYearFocus } = this.state;
      return isDayFocus || isMonthFocus || isYearFocus;
    };

    switch (caseIndex) {
      case 0:
        if (targetVal.length === 1 && targetVal > 0) {
          this.setState({ dayFieldValue: '0' + targetVal });
        } else {
          this.setState({ dayFieldValue: null });
        }
        this.setState({ isDayFocus: false }, () => {
          setTimeout(() => {
            if (!isFocus()) {
              const { questionRel } = this.props;
              blurAction && blurAction(questionRel)
            }
          }, 100);
        });
        break;

      case 1:
        if (targetVal.length === 1 && targetVal > 0) {
          this.setState({ monthFieldValue: '0' + targetVal });
        } else {
          this.setState({ monthFieldValue: null });
        }
        this.setState({ isMonthFocus: false }, () => {
          setTimeout(() => {
            if (!isFocus()) {
              const { questionRel } = this.props;
              blurAction && blurAction(questionRel)
            }
          }, 100);
        });
        break;

      case 2:
        this.setState({ isYearFocus: false }, () => {
          setTimeout(() => {
            if (!isFocus()) {
              const { questionRel } = this.props;
              blurAction && blurAction(questionRel)
            }
          }, 100)
        });
        break;

      default:
        break;
    }
  };

  render() {
    const {
      dayContent,
      monthContent,
      yearContent,
      isError,
      errorMessage,
      content,
      questionRel
    } = this.props;

    /*
    * check if dayFieldValue or monthFieldValue not equal to "null", to set the final value for rendering.
    * */
    if (this.state.dayFieldValue !== null) {
      dayContent.textValue = this.state.dayFieldValue;
    }
    if (this.state.monthFieldValue !== null) {
      monthContent.textValue = this.state.monthFieldValue;
    }

    const titleObj = {
      //question: content.title,
      // questionTip: content.questionTip
      ...content
    };

    return (
      <div className="date-field">
        {/* <div className="item-sub-title" data-key={`${questionRel.questionIndicator}-subtitle`}>
          {content.title}
        </div> */}
        <ItemTitle content={titleObj} />
        <FormGroup validationState={isError ? 'error' : null}>
          <Row className="input-date-wrap combined-group-wrap">
            <Col xs={3} md={2} lg={2} className="group-item day-column">
              <label aria-label={content.title + ' '+dayContent.subtitle.title} title={content.title + ' '+dayContent.subtitle.title} htmlFor={`${questionRel.questionId}_dayField`}>
                <InputText
                  handleTextChange={this.handleDates(0)}
                  content={dayContent}
                  maxLength={2}
                  type={'tel'}
                  handleBlurAction={this.onBlurAction(0)}
                  handleFocusAction={this.onFocusAction(0)}
                  isNeedLabel={false}
                  id={`${questionRel.questionId}_dayField`}
                  ariaDescribedby={`${questionRel.questionId}-fieldError`}
                />
              </label>
            </Col>
            <Col xs={3} md={2} lg={2} className="group-item month-column">
              <label aria-label={content.title + ' '+monthContent.subtitle.title} title={content.title + ' '+monthContent.subtitle.title} htmlFor={`${questionRel.questionId}_monthField`}>
                <InputText
                  handleTextChange={this.handleDates(1)}
                  content={monthContent}
                  maxLength={2}
                  type={'tel'}
                  handleBlurAction={this.onBlurAction(1)}
                  handleFocusAction={this.onFocusAction(1)}
                  isNeedLabel={false}
                  id={`${questionRel.questionId}_monthField`}
                  ariaDescribedby={`${questionRel.questionId}-fieldError`}
                />
              </label>
            </Col>
            <Col xs={4} md={4} lg={4} className="group-item year-column">
              <label aria-label={content.title + ' '+yearContent.subtitle.title} title={content.title + ' '+yearContent.subtitle.title} htmlFor={`${questionRel.questionId}_yearField`}>
                <InputText
                  handleTextChange={this.handleDates(2)}
                  content={yearContent}
                  maxLength={4}
                  type={'tel'}
                  handleBlurAction={this.onBlurAction(2)}
                  handleFocusAction={this.onFocusAction(2)}
                  isNeedLabel={false}
                  id={`${questionRel.questionId}_yearField`}
                  ariaDescribedby={`${questionRel.questionId}-fieldError`}
                />
              </label>
            </Col>
          </Row>

          <HelpBlock id = {`${questionRel.questionId}-fieldError`}>{errorMessage}</HelpBlock>
        </FormGroup>
      </div>
    )
  }
}


DateField.propTypes = {
  dayContent: PropTypes.object.isRequired,
  monthContent: PropTypes.object.isRequired,
  yearContent: PropTypes.object.isRequired,
  dateChangeAction: PropTypes.func,
  isError: PropTypes.bool,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  content: PropTypes.object,
  questionRel: PropTypes.object,
  blurActionObj: PropTypes.object,
};

DateField.defaultProps = {
  itemArray: [],
  disabled: false,
  content: {},
  questionRel: {}
};



// WEBPACK FOOTER //
// ./src/shared/AOForm/components/DateField/index.js